import { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const fipsToAbbr: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO',
  '09': 'CT', '10': 'DE', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID',
  '17': 'IL', '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA',
  '23': 'ME', '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH', '34': 'NJ',
  '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND', '39': 'OH', '40': 'OK',
  '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD', '47': 'TN',
  '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY',
};

const stateNames: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

function roasColor(roas: number | undefined): string {
  if (roas === undefined) return '#1e293b';
  if (roas < 1.5) return '#f43f5e';
  if (roas < 2.0) return '#fb923c';
  if (roas < 2.5) return '#fbbf24';
  if (roas < 3.0) return '#a3e635';
  return '#10b981';
}

interface TooltipState {
  x: number;
  y: number;
  abbr: string;
  roas?: number;
  revenue?: number;
  calls?: number;
}

export function StateMap() {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [stateData, setStateData] = useState<Record<string, { roas: number; revenue: number; calls: number }>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    // Last 30 days
    const endDate = new Date().toISOString().slice(0, 10);
    const startDate = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
    trpc.roas.byGeo.query({ tenantId: user.tenantId, startDate, endDate })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((rows: any[]) => {
        const map: Record<string, { roas: number; revenue: number; calls: number }> = {};
        for (const row of rows) {
          if (row.state) {
            map[row.state.toUpperCase()] = {
              roas: row.roas ?? 0,
              revenue: row.revenue ?? 0,
              calls: row.calls ?? 0,
            };
          }
        }
        setStateData(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">ROAS by State</h3>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-500 inline-block" /> &lt;1.5</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> 1.5–2.5</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> &gt;2.5</span>
          {loading && <span className="text-slate-600 animate-pulse">Loading…</span>}
        </div>
      </div>
      <div className="relative">
        <ComposableMap
          projection="geoAlbersUsa"
          style={{ width: '100%', height: 'auto' }}
          width={800}
          height={500}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map(geo => {
                const fips = geo.id as string;
                const abbr = fipsToAbbr[fips];
                const info = abbr ? stateData[abbr] : undefined;
                const roas = info && info.calls > 0 ? info.roas : undefined;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={roasColor(roas)}
                    stroke="#0f172a"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', opacity: 0.8, cursor: 'pointer' },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={e => {
                      if (abbr) {
                        setTooltip({
                          x: e.clientX,
                          y: e.clientY,
                          abbr,
                          roas,
                          revenue: info?.revenue,
                          calls: info?.calls,
                        });
                      }
                    }}
                    onMouseMove={e => {
                      if (abbr) {
                        setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                      }
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
        {tooltip && (
          <div
            className="fixed z-50 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm shadow-lg pointer-events-none"
            style={{ left: tooltip.x + 12, top: tooltip.y - 40 }}
          >
            <span className="font-medium text-slate-200">{stateNames[tooltip.abbr] ?? tooltip.abbr}</span>
            {tooltip.roas !== undefined && tooltip.roas > 0 ? (
              <span className={`ml-2 font-bold ${tooltip.roas >= 2.5 ? 'text-emerald-400' : tooltip.roas >= 1.5 ? 'text-amber-400' : 'text-rose-400'}`}>
                {tooltip.roas.toFixed(2)}x ROAS
              </span>
            ) : (
              <span className="ml-2 text-slate-500">No data</span>
            )}
            {tooltip.calls !== undefined && tooltip.calls > 0 && (
              <span className="ml-2 text-slate-400 text-xs">{tooltip.calls} calls</span>
            )}
          </div>
        )}
      </div>
      {Object.keys(stateData).length === 0 && !loading && (
        <p className="text-center text-slate-600 text-xs mt-2">No geographic data available for the last 30 days.</p>
      )}
    </div>
  );
}
